import { useEffect, useState, useRef } from 'react'
import { Building2, Check, ChevronDown, Loader2 } from 'lucide-react'
import { useTenantStore } from '@/stores/tenantStore'
import type { Tenant } from '@/types'

const TenantSwitcher = () => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const {
    currentTenant,
    availableTenants,
    isLoading,
    fetchTenants,
    switchTenant
  } = useTenantStore()

  // Load tenants on mount
  useEffect(() => {
    if (availableTenants.length === 0) {
      fetchTenants()
    }
  }, [])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleTenantSwitch = async (tenant: Tenant) => {
    if (tenant.id === currentTenant?.id) {
      setIsOpen(false)
      return
    }

    await switchTenant(tenant.id)
    setIsOpen(false)
  }

  // Don't show if no tenants available
  if (availableTenants.length === 0) {
    return null
  }

  // If only one tenant, show it but make it non-interactive
  const hasMultipleTenants = availableTenants.length > 1

  const getTenantTypeIcon = (type: string) => {
    switch (type) {
      case 'organization':
        return '🏢'
      case 'location':
        return '📍'
      case 'department':
        return '👥'
      default:
        return '🏢'
    }
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => hasMultipleTenants && setIsOpen(!isOpen)}
        disabled={isLoading || !hasMultipleTenants}
        className={`
          flex items-center space-x-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors
          ${isLoading
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
            : hasMultipleTenants
              ? 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300 cursor-pointer'
              : 'bg-white text-gray-700 border border-gray-300 cursor-default'
          }
        `}
        title={hasMultipleTenants ? 'Switch tenant' : 'Current tenant'}
      >
        <Building2 className="w-4 h-4" />
        <span className="max-w-[150px] truncate">
          {currentTenant?.name || 'Select Tenant'}
        </span>
        {isLoading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : hasMultipleTenants ? (
          <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        ) : null}
      </button>

      {/* Dropdown Menu - Only show when there are multiple tenants */}
      {isOpen && !isLoading && hasMultipleTenants && (
        <div className="absolute top-full left-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50 max-h-96 overflow-auto">
          {/* Header */}
          <div className="px-4 py-2 border-b border-gray-100">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Switch Tenant
            </p>
          </div>

          {/* Tenant List */}
          <div className="py-1">
            {availableTenants.map((tenant) => {
              const isActive = tenant.id === currentTenant?.id

              return (
                <button
                  key={tenant.id}
                  onClick={() => handleTenantSwitch(tenant)}
                  className={`
                    w-full flex items-center px-4 py-3 text-sm transition-colors
                    ${isActive
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-gray-700 hover:bg-gray-50'
                    }
                  `}
                >
                  {/* Icon */}
                  <span className="text-lg mr-3">
                    {getTenantTypeIcon(tenant.tenantType)}
                  </span>

                  {/* Tenant Info */}
                  <div className="flex-1 text-left min-w-0">
                    <p className={`font-medium truncate ${isActive ? 'text-primary-700' : 'text-gray-900'}`}>
                      {tenant.name}
                    </p>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className={`text-xs capitalize ${isActive ? 'text-primary-600' : 'text-gray-500'}`}>
                        {tenant.tenantType}
                      </span>
                      {tenant.parentId && (
                        <>
                          <span className="text-gray-300">•</span>
                          <span className="text-xs text-gray-500">
                            Sub-tenant
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Active Indicator */}
                  {isActive && (
                    <Check className="w-4 h-4 text-primary-600 ml-2 flex-shrink-0" />
                  )}
                </button>
              )
            })}
          </div>

          {/* Footer Info */}
          {availableTenants.length === 0 && (
            <div className="px-4 py-3 text-center">
              <p className="text-sm text-gray-500">
                No tenants available
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default TenantSwitcher
